// services/api.js
import axios from "axios";
import { API_BASE_URL } from "../config/apiConfig";
import { FORWARD_ESTIMATE_QUARTERS, normalizeForecastMetricBox, enrichAnalystForecasts } from "../utils/analystForecasts";
import {
  normalizeFundamentalsToUsd,
  normalizeAnalystForecastsToUsd,
} from "../utils/currencyConversion";

/** ----------------------------------------------------------------------------
 * Axios client
 * -------------------------------------------------------------------------- */
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
});

// Optional: simple response un-wrapper (handles {data: {...}} and raw payloads)
function unwrap(resp) {
  return resp?.data?.data ?? resp?.data ?? resp;
}

// Optional: tiny retry-once for transient 5xx/429
async function getWithRetry(path, config, retries = 1) {
  try {
    const resp = await api.get(path, config);
    return unwrap(resp);
  } catch (err) {
    const status = err?.response?.status;
    const transient = status === 429 || (status >= 500 && status < 600);
    if (retries > 0 && transient) {
      await new Promise(r => setTimeout(r, 400));
      return getWithRetry(path, config, retries - 1);
    }
    throw err;
  }
}

/** ----------------------------------------------------------------------------
 * Public API
 * -------------------------------------------------------------------------- */

export async function getPrice(ticker, { withVolume = true, timeseries = 20000 } = {}) {
  return getWithRetry("/price", {
    params: {
      ticker,
      with_volume: withVolume,
      timeseries
    }
  });
}

export async function getFundamentals(ticker) {
  const data = await getWithRetry("/fundamentals", { params: { ticker } });
  return normalizeFundamentalsToUsd(ticker, data || {});
}

export async function getDCFHistory(ticker, years = 5, discount_rate = 0.1) {
  return getWithRetry("/dcf-history", { params: { ticker, years, discount_rate } });
}

export async function getScoresSeries(ticker) {
  // expected shape: { f: [...], z: [...] }
  return getWithRetry("/scores-series", { params: { ticker } });
}

export async function getAnalystForecasts(symbol, analystEstimates = null, fundamentals = null) {
  const url = `${API_BASE_URL}/analyst-forecasts?ticker=${encodeURIComponent(symbol)}`;
  if (process.env.NODE_ENV === "development") {
    console.log("[getAnalystForecasts] Fetching from:", url);
  }
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}: ${res.statusText}`);
  }
  const d = await res.json();
  const fundCtx = fundamentals || { analystEstimates, earningsHistory: null };
  const enriched = enrichAnalystForecasts(
    {
      eps: normalizeForecastMetricBox(d.eps),
      revenue: normalizeForecastMetricBox(d.revenue),
    },
    analystEstimates
  );
  return normalizeAnalystForecastsToUsd(symbol, enriched, fundCtx);
}

/** Analyst forecasts from paginated estimates only (when /analyst-forecasts fails). */
export function buildAnalystForecastsFromFundamentals(symbol, fundamentals) {
  const enriched = enrichAnalystForecasts(null, fundamentals?.analystEstimates);
  return normalizeAnalystForecastsToUsd(symbol, enriched, fundamentals);
}

export async function getAnalystConsensus(ticker) {
  // Returns array of {date, value} where value is normalized between -5 and 5
  return getWithRetry("/analyst-consensus", { params: { ticker } });
}

/** Moody's Aaa corporate bond yield (FRED DAAA). Percent, e.g. 4.5. Empty points → frontend uses fallbackPct. */
export async function getAaaCorporateYield() {
  return getWithRetry("/macro/aaa-yield", {}).catch(() => ({ points: [], fallbackPct: 5 }));
}

/** In-memory promise cache so charts sharing a ticker don't re-hit the API. */
const _companyLogoCache = new Map();

/** FMP company logo URL for a ticker, or null if unavailable. */
export async function getCompanyLogo(ticker) {
  const symbol = String(ticker || "").trim().toUpperCase();
  if (!symbol) return null;
  if (_companyLogoCache.has(symbol)) return _companyLogoCache.get(symbol);
  const pending = getWithRetry("/company-logo", { params: { ticker: symbol } })
    .then((d) => (d?.image ? String(d.image) : null))
    .catch(() => null);
  _companyLogoCache.set(symbol, pending);
  return pending;
}


/** ----------------------------------------------------------------------------
 * Normalizers
 * -------------------------------------------------------------------------- */

/**
 * Normalize the analyst forecasts into:
 * {
 *   eps:     { next8: [{date, estimate}], accuracy5yPct: number|null },
 *   revenue: { next8: [{date, estimate}], accuracy5yPct: number|null }
 * }
 *
 * Handles several common backend shapes:
 * - Array of rows
 * - { data: [...] }
 * - { forecasts: [...] }
 * - { eps: { next8:[...], accuracy5yPct }, revenue: {...} }
 * - Weird key casing (EPS, REVENUE)
 */
function normalizeForecasts(raw) {
  const takeForward = arr => (Array.isArray(arr) ? arr.slice(0, FORWARD_ESTIMATE_QUARTERS) : []);

  const mapRow = (row, type) => {
    const date =
      row?.fiscalDateEnding ??
      row?.fiscalDate ??
      row?.date ??
      row?.period ??
      (row?.calendarYear && row?.period ? `${row.calendarYear}-${row.period}` : null);

    let estimate =
      row?.estimate ??
      row?.value ??
      row?.epsEstimate ??
      row?.eps ??
      row?.estimatedEPSAvg ??
      row?.estimatedEpsAvg ??
      null;

    if (type === "revenue") {
      estimate =
        estimate ??
        row?.revenueEstimate ??
        row?.estimatedRevenueAvg ??
        row?.revenue ??
        null;
    }

    return date && estimate != null ? { date, estimate } : null;
  };

  const box = (items, accuracy) => ({
    next8: takeForward((items || []).filter(Boolean)),
    accuracy5yPct: isFiniteNumber(accuracy) ? Number(accuracy) : null,
  });

  // If raw is an array, try to derive both EPS and Revenue from it
  const fromArray = arr => {
    const epsItems = arr.map(r => mapRow(r, "eps")).filter(Boolean);
    const revItems = arr.map(r => mapRow(r, "revenue")).filter(Boolean);
    return {
      eps: box(epsItems, null),
      revenue: box(revItems, null),
    };
  };

  // Common wrappers
  if (Array.isArray(raw)) return fromArray(raw);
  if (Array.isArray(raw?.data)) return fromArray(raw.data);
  if (Array.isArray(raw?.items)) return fromArray(raw.items);
  if (Array.isArray(raw?.forecasts)) return fromArray(raw.forecasts);

  // Structured { eps: {...}, revenue: {...} } cases
  const epsBox = raw?.eps || raw?.EPS || raw?.data?.eps || {};
  const revBox = raw?.revenue || raw?.REVENUE || raw?.data?.revenue || {};

  const epsItems =
    (epsBox.next8 || epsBox.next4 || epsBox.next || epsBox.items || []).map(r => mapRow(r, "eps")).filter(Boolean);
  const revItems =
    (revBox.next8 || revBox.next4 || revBox.next || revBox.items || []).map(r => mapRow(r, "revenue")).filter(Boolean);

  return {
    eps: box(epsItems, epsBox.accuracy5yPct ?? epsBox.accuracy),
    revenue: box(revItems, revBox.accuracy5yPct ?? revBox.accuracy),
  };
}

/** ----------------------------------------------------------------------------
 * Helpers
 * -------------------------------------------------------------------------- */
function isFiniteNumber(x) {
  const n = Number(x);
  return Number.isFinite(n);
}

export default {
  getPrice,
  getFundamentals,
  getDCFHistory,
  getScoresSeries,
  getAnalystForecasts,
  getAnalystConsensus,
  getAaaCorporateYield,
  getCompanyLogo,
};

// frontend/src/services/api.js
import { API_BASE_URL_NO_PATH } from "../config/apiConfig";
const API = API_BASE_URL_NO_PATH;

async function j(r) {
  if (!r.ok) {
    const text = await r.text();
    throw new Error(text || `HTTP ${r.status}`);
  }
  return r.json();
}

export function getSp500() {
  return fetch(`${API}/api/sp500`).then(j);
}

export function getMLPrice(ticker) {
  const q = encodeURIComponent(ticker);
  return fetch(`${API}/api/ml/price?ticker=${q}`).then(j);
}

export function postMLForecast(payload) {
  return fetch(`${API}/api/ml/forecast`, {
    method: "POST",
    headers: {"Content-Type": "application/json"},
    body: JSON.stringify(payload),
  }).then(j);
}
